import { PositionType } from 'data/types';
import {
  orientCardinalWallEdge,
  resolveOpeningOffsets
} from 'utils/opening-position';
import { OpeningPointType } from 'utils/opening-position-types';
import { EdgeCardinal, SpacePolygonEdge } from 'utils/space-polygon';
import {
  PolygonWallOpeningItemType,
  PolygonWallOpeningSpanType,
  PolygonWallOpeningsSourceType
} from 'utils/space-polygon-wall-opening-types';

export function isGlassDoorFenestration(
  fenestrationType: string | undefined
): boolean {
  return (
    fenestrationType === 'Sliding Glass Door' ||
    fenestrationType === 'French Door'
  );
}

export function wallOpeningTypeFromGlass(
  fenestrationType: string | undefined
): PolygonWallOpeningItemType['type'] {
  return isGlassDoorFenestration(fenestrationType) ? 'glass-door' : 'window';
}

export function getWallOpeningItems(
  wall: PolygonWallOpeningsSourceType
): PolygonWallOpeningItemType[] {
  const items: PolygonWallOpeningItemType[] = [];

  (wall.verticalGlass ?? []).forEach((glass) => {
    if (glass.width > 0) {
      items.push({
        id: glass.uniqueId,
        type: wallOpeningTypeFromGlass(glass.fenestrationType),
        widthFt: glass.width,
        positionOnWall: glass.positionOnWall
      });
    }
  });

  (wall.solidDoors ?? []).forEach((door) => {
    if (door.width > 0) {
      items.push({
        id: door.uniqueId,
        type: 'door',
        widthFt: door.width,
        positionOnWall: door.positionOnWall
      });
    }
  });

  return items;
}

function pointAlongEdge(
  start: OpeningPointType,
  end: OpeningPointType,
  distanceFt: number,
  lengthFt: number
): PositionType {
  if (lengthFt === 0) {
    return { x: start.x, y: start.y };
  }
  const t = distanceFt / lengthFt;
  return {
    x: start.x + (end.x - start.x) * t,
    y: start.y + (end.y - start.y) * t
  };
}

function orientedPolygonEdge(
  p1: PositionType,
  p2: PositionType,
  cardinal: EdgeCardinal | null
): { start: OpeningPointType; end: OpeningPointType; lengthFt: number } {
  const lengthFt = Math.hypot(p2.x - p1.x, p2.y - p1.y);
  if (!cardinal) {
    return { start: p1, end: p2, lengthFt };
  }
  const oriented = orientCardinalWallEdge(p1, p2, cardinal);
  return {
    start: oriented.start,
    end: oriented.end,
    lengthFt
  };
}

export function layoutWallOpeningsOnEdge({
  edge,
  wall
}: {
  edge: SpacePolygonEdge;
  wall: PolygonWallOpeningsSourceType;
}): PolygonWallOpeningSpanType[] {
  const items = getWallOpeningItems(wall);
  if (items.length === 0) {
    return [];
  }

  const oriented = orientedPolygonEdge(edge.p1, edge.p2, edge.cardinal);
  if (oriented.lengthFt <= 0) {
    return [];
  }

  const offsets = resolveOpeningOffsets(
    items.map((item) => ({
      id: item.id,
      widthFt: item.widthFt,
      positionOnWall: item.positionOnWall
    })),
    oriented.lengthFt
  );

  return items.map((item, index) => {
    const widthFt = Math.min(item.widthFt, oriented.lengthFt);
    const offsetFt = offsets[index]?.offsetFt ?? 0;
    return {
      id: item.id,
      type: item.type,
      p1: pointAlongEdge(
        oriented.start,
        oriented.end,
        offsetFt,
        oriented.lengthFt
      ),
      p2: pointAlongEdge(
        oriented.start,
        oriented.end,
        offsetFt + widthFt,
        oriented.lengthFt
      ),
      widthFt
    };
  });
}

export function layoutPolygonWallOpenings({
  edges,
  wallsForEdges
}: {
  edges: SpacePolygonEdge[];
  wallsForEdges: Array<PolygonWallOpeningsSourceType | null>;
}): PolygonWallOpeningSpanType[] {
  const openings: PolygonWallOpeningSpanType[] = [];

  edges.forEach((edge, edgeIndex) => {
    const wall = wallsForEdges[edgeIndex];
    if (!edge.isExterior || !wall) {
      return;
    }
    openings.push(
      ...layoutWallOpeningsOnEdge({
        edge,
        wall
      })
    );
  });

  return openings;
}

export function projectPolygonWallOpenings(
  openings: PolygonWallOpeningSpanType[],
  projectPoint: (point: PositionType) => PositionType
): PolygonWallOpeningSpanType[] {
  return openings.map((opening) => ({
    ...opening,
    p1: projectPoint(opening.p1),
    p2: projectPoint(opening.p2)
  }));
}
